type AdminV2ToolFieldVisibility = {
  adminOnly?: boolean;
  userVisible?: boolean;
  inputRole?: string;
  control?: string;
  defaultValue?: unknown;
  options?: Array<{
    hidden?: boolean;
    value: string | number | boolean;
  }>;
};

export type AdminV2ToolFieldActiveWhen = {
  fieldId: string;
  values: Array<string | number | boolean>;
};

export function isAdminV2ToolFieldActive(
  field: { activeWhen?: AdminV2ToolFieldActiveWhen },
  values: Record<string, unknown>
) {
  return (
    !field.activeWhen ||
    field.activeWhen.values.some((value) =>
      Object.is(value, values[field.activeWhen!.fieldId])
    )
  );
}

/** Inactive scene options use their declared defaults, including in saved jobs. */
export function resetAdminV2InactiveToolFields<
  T extends Record<string, unknown>,
>(
  fields: readonly {
    id: string;
    defaultValue?: unknown;
    activeWhen?: AdminV2ToolFieldActiveWhen;
  }[],
  values: T
): T {
  const result = { ...values };
  for (const field of fields) {
    if (isAdminV2ToolFieldActive(field, values)) continue;
    if (field.defaultValue === undefined) delete result[field.id];
    else (result as Record<string, unknown>)[field.id] = field.defaultValue;
  }
  return result;
}

export function isAdminV2UserFacingToolField(
  field: AdminV2ToolFieldVisibility
) {
  return (
    field.userVisible !== false &&
    field.adminOnly !== true &&
    field.inputRole !== 'internal'
  );
}

export function isAdminV2UserFacingToolOption(option: { hidden?: boolean }) {
  return option.hidden !== true;
}

export function getAdminV2UserFacingToolOptions<
  TOption extends { hidden?: boolean },
>(options: readonly TOption[] | undefined) {
  return (options ?? []).filter(isAdminV2UserFacingToolOption);
}

export function isAdminV2UserFacingToolOptionValue(
  field: AdminV2ToolFieldVisibility,
  value: unknown
) {
  return getAdminV2UserFacingToolOptions(field.options).some((option) =>
    Object.is(option.value, value)
  );
}

export function getAdminV2UserFacingToolFieldValue(params: {
  field: AdminV2ToolFieldVisibility;
  value: unknown;
}) {
  if (params.value === undefined) {
    return undefined;
  }

  if (params.field.control === 'select') {
    return isAdminV2UserFacingToolOptionValue(params.field, params.value)
      ? params.value
      : undefined;
  }

  if (params.field.control === 'multi-select') {
    if (!Array.isArray(params.value)) {
      return undefined;
    }
    const visibleValues = params.value.filter((value) =>
      isAdminV2UserFacingToolOptionValue(params.field, value)
    );
    return visibleValues.length > 0 ? visibleValues : undefined;
  }

  return params.value;
}

export function getAdminV2UserFacingToolFieldDefaultValue(
  field: AdminV2ToolFieldVisibility
) {
  return getAdminV2UserFacingToolFieldValue({
    field,
    value: field.defaultValue,
  });
}

export function toAdminV2UserFacingToolField<
  TField extends AdminV2ToolFieldVisibility,
>(field: TField): TField {
  const sanitized = structuredClone(field) as TField;

  if (Array.isArray(sanitized.options)) {
    sanitized.options = getAdminV2UserFacingToolOptions(
      sanitized.options
    ) as TField['options'];
  }

  const defaultValue = getAdminV2UserFacingToolFieldDefaultValue(field);
  if (defaultValue === undefined) {
    const { defaultValue: _defaultValue, ...fieldWithoutDefaultValue } =
      sanitized;
    return fieldWithoutDefaultValue as TField;
  }

  sanitized.defaultValue = structuredClone(defaultValue);

  return sanitized;
}
